import React, { useState, useMemo, useEffect } from 'react';
import { getCategories, getGalleryImages, trackImageView } from '../../services/api';
import Lightbox from '../shared/Lightbox';
import useReveal from '../../hooks/useReveal';

const Gallery = () => {
  const [categories, setCategories] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeFilter, setActiveFilter] = useState(() => {
    const saved = sessionStorage.getItem('xp-filter');
    if (saved) {
      sessionStorage.removeItem('xp-filter');
      return saved;
    }
    return 'all';
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, imagesData] = await Promise.all([
          getCategories(),
          getGalleryImages()
        ]);
        setCategories(categoriesData);
        setGalleryImages(imagesData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading gallery data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredImages = useMemo(() => {
    if (activeFilter === 'all') {
      return galleryImages;
    }
    // Filter images that have the selected category in their category array
    return galleryImages.filter(img => {
      if (Array.isArray(img.category)) {
        return img.category.includes(activeFilter);
      }
      // Backward compatibility for single category
      return img.category === activeFilter;
    });
  }, [activeFilter, galleryImages]);

  useReveal([loading, filteredImages]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
    const img = filteredImages[index];
    if (img?.id) trackImageView(img.id);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prevIndex) => {
      const next = (prevIndex + 1) % filteredImages.length;
      const img = filteredImages[next];
      if (img?.id) trackImageView(img.id);
      return next;
    });
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prevIndex) => {
      const next = prevIndex === 0 ? filteredImages.length - 1 : prevIndex - 1;
      const img = filteredImages[next];
      if (img?.id) trackImageView(img.id);
      return next;
    });
  };

  if (loading) {
    return (
      <section className="section-container" id="gallery">
        <h2 className="section-title">Portfolio Gallery</h2>
        <p className="section-subtitle">Loading gallery...</p>
      </section>
    );
  }

  return (
    <section className="section-container" id="gallery">
      <h2 className="section-title" data-reveal>Portfolio Gallery</h2>
      <p className="section-subtitle" data-reveal style={{ '--reveal-delay': '120ms' }}>
        Explore my diverse collection of photography work spanning events, portraits, 
        landscapes, commercial projects, and fine art.
      </p>

      <div className="gallery-filter" data-reveal style={{ '--reveal-delay': '240ms' }}>
        {categories.map((category) => (
          <button
            key={category.id}
            className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
            onClick={() => setActiveFilter(category.id)}
            data-gas
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className="gallery-grid">
        {filteredImages.map((image, index) => (
          <div
            key={image.id}
            className="gallery-item"
            data-reveal
            style={{ '--reveal-delay': `${(index % 3) * 140}ms` }}
            onClick={() => handleImageClick(index)}
          >
            <img src={image.thumbnail_url} alt={image.title || 'Gallery image'} loading="lazy" />
            <div className="gallery-item-overlay">
              {image.title && <h3 className="gallery-item-title">{image.title}</h3>}
              <p className="gallery-item-desc">{image.description}</p>
            </div>
          </div>
        ))}
      </div>

      {lightboxOpen && (
        <Lightbox
          images={filteredImages}
          currentIndex={selectedImageIndex}
          onClose={handleCloseLightbox}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}
    </section>
  );
};

export default Gallery;